import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { api, getToken, setToken } from "@/lib/api";
import type { LoginResponse } from "@/lib/types";

export type UserRole = "admin" | "viewer";

type AuthState = {
  token: string | null;
  username: string | null;
  role: UserRole;
};

type AuthContextValue = AuthState & {
  isAdmin: boolean;
  login: (username: string, password: string) => Promise<LoginResponse>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

type JwtPayload = {
  sub?: unknown;
  role?: unknown;
};

function decodeJwt(token: string | null): JwtPayload | null {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    return JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
}

function decodeUsername(token: string | null): string | null {
  const payload = decodeJwt(token);
  return typeof payload?.sub === "string" ? payload.sub : null;
}

/**
 * Rolle aus dem JWT extrahieren. Fehlt das Feld (alter Token vor dem Rollen-
 * Feature), defaulten wir auf "admin" — Backward-Compat zum Backend-Verhalten,
 * sonst würden bestehende Sessions auf einen Schlag rausfliegen.
 */
function decodeRole(token: string | null): UserRole {
  const payload = decodeJwt(token);
  return payload?.role === "viewer" ? "viewer" : "admin";
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setTok] = useState<string | null>(() => getToken());

  useEffect(() => {
    const onChange = () => setTok(getToken());
    window.addEventListener("leif:token-changed", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("leif:token-changed", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const resp = await api.post<LoginResponse>("/api/login", { username, password });
    if (resp.success && resp.token) {
      setToken(resp.token);
      setTok(resp.token);
    }
    return resp;
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setTok(null);
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const role = decodeRole(token);
    return {
      token,
      username: decodeUsername(token),
      role,
      isAdmin: role === "admin",
      login,
      logout,
    };
  }, [token, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth muss innerhalb von <AuthProvider> stehen");
  return ctx;
}
